import React, { useState, useEffect } from 'react';
import { Button, Form, Card, Alert, Row, Col } from 'react-bootstrap';
import { User, Mail, Phone, Check, Save, X } from 'lucide-react';
import useForm from '../../hooks/useForm';
import { createUser, getUserById, updateUser } from '../../api/users';
import { Role } from '../../utils/constants';

const UserForm = ({ userId, onCancel, onSuccess }) => {
    const isEditMode = !!userId;
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [generatedPassword, setGeneratedPassword] = useState('');

    const initialValues = {
        username: '',
        fullName: '',
        email: '',
        phoneNumber: '',
        active: true,
        roles: []
    };

    const { values, setValues, handleChange, errors, validateForm } = useForm(initialValues);

    useEffect(() => {
        if (isEditMode) {
            fetchUserData();
        }
    }, [userId]);

    const fetchUserData = async () => {
        try {
            setLoading(true);
            const userData = await getUserById(userId);

            // Transform roles array to match form structure
            const roleTypes = (userData.roles || []).map(r => (typeof r === 'string' ? r : r.name));
            setValues({
                username: userData.username,
                fullName: userData.fullName,
                email: userData.email,
                phoneNumber: userData.phoneNumber || '',
                active: userData.active,
                roles: roleTypes
            });
        } catch (err) {
            setError('Failed to load user data. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const validationSchema = {
        username: {
            required: true,
            min: 3,
            errorMessage: 'Username is required (min 3 characters)'
        },
        fullName: {
            required: true,
            errorMessage: 'Full name is required'
        },
        email: {
            required: true,
            pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            errorMessage: 'Valid email is required'
        },
        roles: {
            required: true,
            errorMessage: 'At least one role must be selected'
        }
    };

    const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');

    if (!validateForm(validationSchema)) { setLoading(false); return; }

    if (!values.roles || values.roles.length === 0) {
        setError('Au moins un rôle doit être sélectionné');
        setLoading(false);
        return;
    }

    try {
        if (isEditMode) {
            await updateUser(userId, {
                fullName: values.fullName,
                email: values.email,
                phoneNumber: values.phoneNumber,
                active: values.active,
                roles: values.roles            // tableau de noms
            });
            setSuccess('Utilisateur modifié avec succès!');
        } else {
            const result = await createUser({ ...values, roles: values.roles });
            if (result && result.password) setGeneratedPassword(result.password);
            setSuccess("L'utilisateur est créé avec succès!");
        }
        setTimeout(() => { if (onSuccess) onSuccess(); }, 1500);
    } catch (err) {
        setError(err.message || "Une erreur s'est produite. Veuillez réessayer.");
        console.error(err);
    } finally {
        setLoading(false);
    }
};

    const handleRoleChange = (e) => {
    const { name, checked } = e.target;
    setValues(prev => ({
        ...prev,
        roles: checked
            ? [...prev.roles, name]
            : prev.roles.filter(r => r !== name)
    }));
};

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">{isEditMode ? 'Modifier Utilisateur' : 'Ajouter utilisateur'}</h5>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                {generatedPassword && (
                    <Alert variant="info">
                        <strong>Génèrer mot de passe:</strong> {generatedPassword}
                        <p className="mt-2 mb-0">
                          Veuillez enregistrer ce mot de passe ou le partager avec l'utilisateur. Il devra être modifié lors de la première connexion
                        </p>
                    </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nom d'ulisateur</Form.Label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <User size={18} />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        name="username"
                                        value={values.username}
                                        onChange={handleChange}
                                        placeholder="Entrer nom d'ulilisateur"
                                        disabled={isEditMode}
                                        required
                                    />
                                </div>
                                {errors.username && <Form.Text className="text-danger">{errors.username}</Form.Text>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nom Complet</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="fullName"
                                    value={values.fullName}
                                    onChange={handleChange}
                                    placeholder="Entrer nom complet"
                                    required
                                />
                                {errors.fullName && <Form.Text className="text-danger">{errors.fullName}</Form.Text>}
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Mail size={18} />
                                    </span>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        placeholder="Entrer email"
                                        required
                                    />
                                </div>
                                {errors.email && <Form.Text className="text-danger">{errors.email}</Form.Text>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Téléphone</Form.Label>
                                <div className="input-group">
                                    <span className="input-group-text">
                                        <Phone size={18} />
                                    </span>
                                    <Form.Control
                                        type="text"
                                        name="phoneNumber"
                                        value={values.phoneNumber}
                                        onChange={handleChange}
                                        placeholder="Entrer numéro téléphone"
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-3">
                        <Form.Label>Statut</Form.Label>
                        <Form.Check
                            type="switch"
                            id="user-active"
                            name="active"
                            label={values.active ? "Actif" : "Inactif"}
                            checked={values.active}
                            onChange={handleChange}
                        />
                    </Form.Group>

                    <Form.Group className="mb-4">
                        <Form.Label>Roles</Form.Label>
                        <div className="d-flex flex-wrap gap-3">
                            {Object.values(Role).map((role) => (
                            <Form.Check
                                key={role.value}
                                type="checkbox"
                                id={`role-${role.value}`}
                                name={role.value}
                                label={role.label}
                                checked={values.roles.includes(role.value)}
                                onChange={handleRoleChange}
                            />
                        ))}
                        </div>
                        {errors.roles && <Form.Text className="text-danger">{errors.roles}</Form.Text>}
                    </Form.Group>

                    <div className="d-flex justify-content-end gap-2">
                        <Button
                            variant="outline-secondary"
                            onClick={onCancel}
                            disabled={loading}
                        >
                            <X size={16} className="me-1" />
                            Annuler
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    {isEditMode ? 'Modification...' : 'Création...'}
                                </>
                            ) : (
                                <>
                                    <Save size={16} className="me-1" />
                                    {isEditMode ? 'Modifier utilisateur' : 'Créer utilisateur'}
                                </>
                            )}
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
};

export default UserForm;